import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    AppBar,
    Toolbar,
    Button,
    Box,
    Container,
    IconButton,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    Menu,
    MenuItem,
    Typography,
    BottomNavigation,
    BottomNavigationAction,
    Paper,
    useTheme,
    useMediaQuery,
    styled,
    keyframes,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import Logo from "../images/logo.png";
import {
    Info as AboutIcon,
    Build as ServicesIcon,
    Groups as ClientsIcon,
    Code as WorkflowIcon,
    Home as HomeIcon,
    ContactSupport as ContactIcon,
    MoreVert as MoreIcon,
    MoreHoriz as MoreHorizIcon,
    People as TeamIcon,
    PhotoLibrary as GalleryIcon,
    School as InternIcon,
    Folder as ProjectsIcon, 
    EmojiEvents as LogoIcon,  
} from '@mui/icons-material';

/* ------------------------------------------------------------------ */
/*  Layout constants                                                  */
/*  Export these so the page-level layout can reserve space for the   */
/*  fixed bottom navigation on mobile and avoid content being hidden  */
/*  behind it.                                                        */
/* ------------------------------------------------------------------ */
export const MOBILE_BOTTOM_NAV_HEIGHT = 64;

export const MobileBottomNavSpacer = () => (
    <Box
        sx={{
            display: { xs: 'block', md: 'none' },
            height: `calc(${MOBILE_BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
        }}
        aria-hidden="true"
    />
);

/* ------------------------------------------------------------------ */
/*  Desktop visuals — UNCHANGED from the original component.          */
/*  Rule #1 forbids altering desktop structure, so every styled       */
/*  primitive below is identical to the existing implementation.      */
/* ------------------------------------------------------------------ */
const pulseLogo = keyframes`
    0% { transform: scale(1); filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.21)) brightness(1.0) contrast(1.1); }
    50% { transform: scale(1.05); filter: drop-shadow(2px 2px 6px rgba(0,0,0,0.3)) brightness(1.1) contrast(1.2); }
    100% { transform: scale(1); filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.21)) brightness(1.0) contrast(1.1); }
`;

const StyledAppBar = styled(AppBar)(({ theme, scrolled, textColor }) => ({
    position: 'fixed',
    backgroundColor: scrolled ? 'rgba(0, 0, 0, 0.6)' : 'transparent',
    color: textColor,
    boxShadow: 'none',
    transition: 'background-color 0.3s ease, backdrop-filter 0.3s ease',
    backdropFilter: scrolled ? 'blur(10px)' : 'none',
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    padding: theme.spacing(0, 2),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0, 1),
    },
    // Compact height on mobile only — premium, non-oversized header.
    // Desktop height/structure is untouched.
    [theme.breakpoints.down('md')]: {
        minHeight: 56,
    },
}));

const LogoLink = styled(Link)({
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    '& img': {
        height: '40px',
        width: 'auto',
        marginRight: '12px',
        animation: `${pulseLogo} 3s infinite alternate`,
        transition: 'all 0.3s ease',
        '&:hover': {
            filter: 'drop-shadow(2px 2px 6px rgba(0,0,0,0.3)) brightness(1.1) contrast(1.2)',
        },
    },
});

const DesktopNavBox = styled(Box)(({ theme }) => ({
    display: 'none',
    alignItems: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
        display: 'flex',
    },
}));

const HeaderButton = styled(Button)(({ theme, textColor, active }) => ({
    color: textColor,
    textTransform: 'none',
    fontWeight: active ? 600 : 500,
    fontSize: '1rem',
    padding: theme.spacing(1, 2),
    borderRadius: 12,
    backgroundColor: active ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
    transition: 'background-color 0.2s ease, font-weight 0.2s ease',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
}));

const SubMenuItem = styled(MenuItem)(({ theme }) => ({
    borderRadius: 8,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

/* ------------------------------------------------------------------ */
/*  Mobile-only visuals — Neumorphic bottom navigation system.        */
/* ------------------------------------------------------------------ */

// Neumorphic shadow helpers
const neumorphShadow = (intensity = 0.6) => ({
    boxShadow: `${9 * intensity}px ${9 * intensity}px ${16 * intensity}px rgba(163, 177, 198, ${0.6 * intensity}), -${9 * intensity}px -${9 * intensity}px ${16 * intensity}px rgba(255, 255, 255, ${0.8 * intensity})`
});

const neumorphInset = (intensity = 0.6) => ({
    boxShadow: `inset ${6 * intensity}px ${6 * intensity}px ${12 * intensity}px rgba(163, 177, 198, ${0.5 * intensity}), inset -${6 * intensity}px -${6 * intensity}px ${12 * intensity}px rgba(255, 255, 255, ${0.7 * intensity})`
});

const BottomNavWrapper = styled(Paper)(({ theme }) => ({
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.appBar + 1,
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    backgroundColor: '#e0e5ec',
    border: 'none',
    borderRadius: '24px 24px 0 0',
    ...neumorphShadow(0.5),
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    willChange: 'transform',
    '&:hover': {
        ...neumorphShadow(0.7),
    },
}));

const MoreSheetHandle = styled(Box)(({ theme }) => ({
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.palette.divider,
    margin: '0 auto',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.8), 0 1px 2px rgba(163,177,198,0.3)',
}));

// Neumorphic Bottom Navigation Action
const NeumorphicBottomNavAction = styled(BottomNavigationAction)(({ theme }) => ({
    borderRadius: 12,
    margin: '4px 2px',
    transition: 'all 0.3s ease',
    backgroundColor: 'transparent',
    '&.Mui-selected': {
        backgroundColor: '#e0e5ec',
        ...neumorphInset(0.4),
        '& .MuiBottomNavigationAction-label': {
            fontWeight: 600,
            color: '#2c3e50',
        },
        '& .MuiSvgIcon-root': {
            color: '#2c3e50',
        },
    },
    '&:hover': {
        backgroundColor: 'rgba(224, 229, 236, 0.6)',
    },
    '& .MuiBottomNavigationAction-label': {
        fontSize: '0.65rem',
        transition: 'all 0.3s ease',
        color: '#4a5b6e',
    },
    '& .MuiSvgIcon-root': {
        transition: 'all 0.3s ease',
        color: '#4a5b6e',
        fontSize: '1.5rem',
    },
    '&:not(.Mui-selected):hover': {
        transform: 'translateY(-2px)',
        ...neumorphShadow(0.3),
    },
    '&:active': {
        transform: 'scale(0.92)',
        ...neumorphInset(0.3),
    },
}));

// Neumorphic Drawer for More sheet
const StyledDrawer = styled(Drawer)(({ theme }) => ({
    '& .MuiPaper-root': {
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        maxHeight: '70vh',
        backgroundColor: '#e0e5ec',
        ...neumorphShadow(0.6),
        border: 'none',
    },
}));

// Neumorphic List Item Button
const NeumorphicListItemButton = styled(ListItemButton)(({ theme, selected }) => ({
    borderRadius: 12,
    margin: '2px 8px',
    transition: 'all 0.3s ease',
    backgroundColor: selected ? '#e0e5ec' : 'transparent',
    ...(selected && neumorphInset(0.3)),
    '&:hover': {
        backgroundColor: '#e0e5ec',
        ...neumorphShadow(0.3),
    },
    '&:active': {
        transform: 'scale(0.96)',
        ...neumorphInset(0.3),
    },
    '& .MuiListItemIcon-root': {
        color: selected ? '#2c3e50' : '#4a5b6e',
        minWidth: 40,
    },
    '& .MuiListItemText-primary': {
        fontWeight: selected ? 600 : 400,
        color: selected ? '#2c3e50' : '#4a5b6e',
    },
}));

// Neumorphic Divider
const NeumorphicDivider = styled(Divider)(({ theme }) => ({
    borderColor: 'rgba(163, 177, 198, 0.3)',
    boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.5)',
    '&::before, &::after': {
        borderColor: 'rgba(163, 177, 198, 0.3)',
    },
}));

// Neumorphic Close Button
const NeumorphicIconButton = styled(IconButton)(({ theme }) => ({
    borderRadius: 12,
    backgroundColor: '#e0e5ec',
    ...neumorphShadow(0.3),
    transition: 'all 0.3s ease',
    '&:hover': {
        ...neumorphShadow(0.5),
        transform: 'scale(1.05)',
    },
    '&:active': {
        transform: 'scale(0.92)',
        ...neumorphInset(0.3),
    },
    '& .MuiSvgIcon-root': {
        color: '#4a5b6e',
    },
}));

const Navbar = () => {
    const theme = useTheme();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [moreAnchorEl, setMoreAnchorEl] = useState(null);
    const [scrolled, setScrolled] = useState(false);
    const [textColor, setTextColor] = useState(theme.palette.common.white);
    const [moreSheetOpen, setMoreSheetOpen] = useState(false);
    const [bottomNavHidden, setBottomNavHidden] = useState(
        () => typeof window !== 'undefined' && window.scrollY === 0 && location.pathname === '/'
    );

    const scrollTimeoutRef = useRef(null);

    /* ---------------- Active-route helper (shared everywhere) ------- */
    const isActive = useCallback((path) => location.pathname === path, [location.pathname]);

    /* ---------------- Single source of truth for nav config --------- */
    // Desktop list includes original five items plus Projects and Logo
    const desktopNavItems = useMemo(
        () => [
            { text: 'Home', path: '/', icon: <HomeIcon /> },
            { text: 'Services', path: '/services', icon: <ServicesIcon /> },
            { text: 'Workflow', path: '/workflow', icon: <WorkflowIcon /> },
            { text: 'About', path: '/about', icon: <AboutIcon /> },
            { text: 'Clients', path: '/clients', icon: <ClientsIcon /> },
            { text: 'Projects', path: '/projects', icon: <ProjectsIcon /> }, // Added
  
        ],
        []
    );

    // Desktop "More" dropdown is unchanged from the original implementation.
    const desktopMoreItems = useMemo(
        () => [
            { text: 'Our Team', path: '/team', icon: <TeamIcon /> },
            { text: 'Intern', path: '/intern', icon: <InternIcon /> },
            { text: 'Gallery', path: '/gallery', icon: <GalleryIcon /> },
            { text: 'Contact Us', path: '/contact', icon: <ContactIcon /> }, 
            { text: 'Logo', path: '/logo', icon: <LogoIcon /> }, // Added
        ],
        []
    );

    // Mobile bottom nav: max 4 actions — Home, Services, Projects, More.
    const primaryMobileNav = useMemo(
        () => [
            { text: 'Home', path: '/', icon: <HomeIcon /> },
            { text: 'Services', path: '/services', icon: <ServicesIcon /> },
            { text: 'Projects', path: '/projects', icon: <ProjectsIcon /> }, // Added (replaced Intern)
        ],
        []
    );

    // Everything not in the primary tabs lives in the mobile "More" sheet.
    const mobileMoreItems = useMemo(
        () => [
            { text: 'Workflow', path: '/workflow', icon: <WorkflowIcon /> },
            { text: 'About', path: '/about', icon: <AboutIcon /> },
            { text: 'Clients', path: '/clients', icon: <ClientsIcon /> },
            { text: 'Our Team', path: '/team', icon: <TeamIcon /> },
            { text: 'Intern', path: '/intern', icon: <InternIcon /> }, // Moved to More
            { text: 'Gallery', path: '/gallery', icon: <GalleryIcon /> },
            { text: 'Contact Us', path: '/contact', icon: <ContactIcon /> },
            { text: 'Logo', path: '/logo', icon: <LogoIcon /> }, // Added to More
        ],
        []
    );

    const bottomNavValue = useMemo(() => {
        if (primaryMobileNav.some((item) => item.path === location.pathname)) {
            return location.pathname;
        }
        if (mobileMoreItems.some((item) => item.path === location.pathname)) {
            return 'more';
        }
        return false;
    }, [location.pathname, primaryMobileNav, mobileMoreItems]);

    /* ---------------- Scroll handling (debounced, passive) ----------- */
    const recomputeFromScroll = useCallback(() => {
        const isScrolled = window.scrollY > 50;
        setScrolled(isScrolled);
        setTextColor(
            isScrolled
                ? theme.palette.common.white
                : location.pathname === '/'
                ? theme.palette.common.white
                : theme.palette.text.primary
        );

        // Mandatory visibility rule: hidden ONLY when both are true.
        const shouldHide = window.scrollY === 0 && location.pathname === '/';
        setBottomNavHidden(shouldHide);
    }, [location.pathname, theme]);

    useEffect(() => {
        // Run once immediately so route changes (without a scroll event)
        // still update bar color and bottom-nav visibility correctly.
        recomputeFromScroll();

        const handleScroll = () => {
            if (scrollTimeoutRef.current) return;
            scrollTimeoutRef.current = window.setTimeout(() => {
                recomputeFromScroll();
                scrollTimeoutRef.current = null;
            }, 50);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = null;
            }
        };
    }, [recomputeFromScroll]);

    /* ---------------- Handlers ---------------------------------------- */
    const handleMoreClick = useCallback((event) => {
        setMoreAnchorEl(event.currentTarget);
    }, []);

    const handleMoreClose = useCallback(() => {
        setMoreAnchorEl(null);
    }, []);

    const handleOpenMoreSheet = useCallback((event) => {
        event.preventDefault();
        setMoreSheetOpen(true);
    }, []);

    const handleCloseMoreSheet = useCallback(() => {
        setMoreSheetOpen(false);
    }, []);

    return (
        <>
            {/* ============== APP BAR (desktop structure preserved) ========== */}
            <StyledAppBar position="fixed" scrolled={scrolled} textColor={textColor}>
                <Container maxWidth="xl">
                    <StyledToolbar>
                        <LogoLink to="/">
                            <img src={Logo} alt="AediaX Logo" />
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    color: textColor,
                                    display: { xs: 'none', sm: 'block' },
                                }}
                            >
                                AediaX
                            </Typography>
                        </LogoLink>

                        <Box sx={{ flexGrow: 1 }} />

                        {/* Desktop navigation — unchanged layout, only the new
                            Projects and Logo routes and active-state styling were added. */}
                        <DesktopNavBox component="nav" aria-label="Primary navigation">
                            {desktopNavItems.map((item) => (
                                <HeaderButton
                                    key={item.text}
                                    component={Link}
                                    to={item.path}
                                    startIcon={item.icon}
                                    textColor={textColor}
                                    active={isActive(item.path)}
                                    aria-current={isActive(item.path) ? 'page' : undefined}
                                >
                                    {item.text}
                                </HeaderButton>
                            ))}

                            <HeaderButton
                                onClick={handleMoreClick}
                                startIcon={<MoreIcon />}
                                textColor={textColor}
                                aria-label="More navigation options"
                                aria-haspopup="true"
                                aria-expanded={Boolean(moreAnchorEl)}
                            >
                                {''}
                            </HeaderButton>
                            <Menu
                                anchorEl={moreAnchorEl}
                                open={Boolean(moreAnchorEl)}
                                onClose={handleMoreClose}
                                sx={{ mt: 1 }}
                            >
                                {desktopMoreItems.map((item) => (
                                    <SubMenuItem
                                        key={item.text}
                                        component={Link}
                                        to={item.path}
                                        onClick={handleMoreClose}
                                        selected={isActive(item.path)}
                                        aria-current={isActive(item.path) ? 'page' : undefined}
                                    >
                                        <ListItemIcon sx={{ color: 'inherit' }}>{item.icon}</ListItemIcon>
                                        <ListItemText primary={item.text} />
                                    </SubMenuItem>
                                ))}
                            </Menu>
                        </DesktopNavBox>
                    </StyledToolbar>
                </Container>
            </StyledAppBar>

            {/* ============== MOBILE BOTTOM NAVIGATION (Neumorphic) ================= */}
            {isMobile && (
                <>
                    <BottomNavWrapper
                        elevation={0}
                        role="navigation"
                        aria-label="Primary mobile navigation"
                        sx={{
                            transform: bottomNavHidden ? 'translateY(100%)' : 'translateY(0)',
                        }}
                    >
                        <BottomNavigation
                            showLabels
                            value={bottomNavValue}
                            sx={{ 
                                height: MOBILE_BOTTOM_NAV_HEIGHT,
                                backgroundColor: 'transparent',
                            }}
                        >
                            {primaryMobileNav.map((item) => (
                                <NeumorphicBottomNavAction
                                    key={item.text}
                                    label={item.text}
                                    value={item.path}
                                    icon={item.icon}
                                    component={Link}
                                    to={item.path}
                                    aria-label={item.text}
                                    aria-current={isActive(item.path) ? 'page' : undefined}
                                />
                            ))}
                            <NeumorphicBottomNavAction
                                label="More"
                                value="more"
                                icon={<MoreHorizIcon />}
                                onClick={handleOpenMoreSheet}
                                aria-label="More navigation options"
                                aria-haspopup="true"
                                aria-expanded={moreSheetOpen}
                            />
                        </BottomNavigation>
                    </BottomNavWrapper>

                    {/* "More" bottom sheet — secondary destinations live here */}
                    <StyledDrawer
                        anchor="bottom"
                        open={moreSheetOpen}
                        onClose={handleCloseMoreSheet}
                        ModalProps={{ keepMounted: true }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pt: 1.5, pr: 2 }}>
                            <NeumorphicIconButton
                                onClick={handleCloseMoreSheet}
                                aria-label="Close more options"
                                size="small"
                            >
                                <CloseIcon fontSize="small" />
                            </NeumorphicIconButton>
                        </Box>
                        <MoreSheetHandle sx={{ mt: -2, mb: 1.5 }} />
                        <Typography
                            variant="subtitle2"
                            sx={{ px: 3, py: 1, color: '#4a5b6e', fontWeight: 600 }}
                        >
                            More
                        </Typography>
                        <NeumorphicDivider />
                        <List sx={{ pb: 2, pt: 1 }} aria-label="More navigation options">
                            {mobileMoreItems.map((item) => (
                                <NeumorphicListItemButton
                                    key={item.text}
                                    component={Link}
                                    to={item.path}
                                    selected={isActive(item.path)}
                                    onClick={handleCloseMoreSheet}
                                    aria-current={isActive(item.path) ? 'page' : undefined}
                                >
                                    <ListItemIcon>{item.icon}</ListItemIcon>
                                    <ListItemText primary={item.text} />
                                </NeumorphicListItemButton>
                            ))}
                        </List>
                    </StyledDrawer>
                </>
            )}
        </>
    );
};

export default React.memo(Navbar);